/**
 * Carey — patient AI assistant. Conventions for identifying and rendering Carey-authored chat
 * messages and the structured "artifacts" they can carry (approval cards, health-record cards).
 * Carey rides on the existing chat infrastructure (room_type: 'carey'); artifacts are tagged via
 * Message.attributes. This is the single source of truth consumed by ChatUtils (message grouping)
 * and ChatMessage (rendering).
 *
 * Mirrors the backend contract in homemd-server: MessageAttributes.artifact_* and the
 * /chat/carey/approvals/{requestId} endpoints.
 */

/** Value of `ChatArchive.room_type` for a Carey room. */
export const CAREY_ROOM_TYPE = 'carey';

/** Value of `Message.attributes.author_role` on Carey-authored messages. */
export const CAREY_AUTHOR_ROLE = 'carey';

/** Display name shown for Carey-authored messages. */
export const CAREY_DISPLAY_NAME = 'Carey';

/** Values of `Message.attributes.artifact_type`. */
export const CAREY_ARTIFACT = {
  approvalRequest: 'approval_request',
  healthRecord: 'health_record',
  appointments: 'appointments',
  documents: 'documents',
  immunizations: 'immunizations',
  handoff: 'handoff',
  documentReceived: 'document_received',
  subscription: 'subscription',
  choosePrimary: 'choose_primary',
  cancellationList: 'cancellation_list',
} as const;

/** Values of `Message.attributes.artifact_status` for approval artifacts. */
export const CAREY_APPROVAL_STATUS = {
  pending: 'pending',
  approved: 'approved',
  denied: 'denied',
  executed: 'executed',
  expired: 'expired',
} as const;

type WithAttrs = { attributes?: any } | undefined;

/** True if a message is authored by Carey (based on its attributes tag). */
export const isCareyMessage = (message: WithAttrs): boolean =>
  message?.attributes?.author_role === CAREY_AUTHOR_ROLE;

export const getArtifactType = (message: WithAttrs): string | undefined =>
  message?.attributes?.artifact_type;

export const getArtifactId = (message: WithAttrs): string | undefined =>
  message?.attributes?.artifact_id;

export const getArtifactStatus = (message: WithAttrs): string | undefined =>
  message?.attributes?.artifact_status;

export const getArtifactPayload = (message: WithAttrs): any =>
  message?.attributes?.artifact_payload ?? {};

/** True if the message is an action-approval request (book/reschedule/cancel). */
export const isApprovalRequest = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.approvalRequest;

/** True if an approval request is still awaiting the patient's decision. */
export const isPendingApproval = (message: WithAttrs): boolean =>
  isApprovalRequest(message) && getArtifactStatus(message) === CAREY_APPROVAL_STATUS.pending;

/**
 * Unix expiry of an approval card, if any. The backend stamps `expires_at` on the payload only for
 * time-sensitive cards (book/reschedule) whose proposed slot isn't held; cancel/pharmacy/refill
 * cards reference stable ids and never expire. Mirrors CAREY_APPROVAL_EXPIRY_SECONDS server-side.
 */
export const getApprovalExpiresAt = (message: WithAttrs): number | undefined => {
  const expiresAt = getArtifactPayload(message)?.expires_at;
  return typeof expiresAt === 'number' ? expiresAt : undefined;
};

/**
 * True if a still-pending approval card has passed its expiry. Lets the client render the card as
 * expired and drop the Approve button before the patient taps a dead card — the server enforces the
 * same expiry on tap, so this is a UX nicety, not the source of truth. `nowUnix` is injected so a
 * timer can re-evaluate it as the clock crosses expires_at.
 */
export const isApprovalExpired = (message: WithAttrs, nowUnix: number): boolean => {
  const expiresAt = getApprovalExpiresAt(message);
  return expiresAt !== undefined && nowUnix > expiresAt;
};

/** True if the message is a health-record summary card. */
export const isHealthRecordArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.healthRecord;

/** True if the message is an appointments card. */
export const isAppointmentsArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.appointments;

/** True if the message is a documents card. */
export const isDocumentsArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.documents;

/** True if the message is an immunizations PDF card. */
export const isImmunizationsArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.immunizations;

/** True if the message is a handoff (chat transitioned to staff) card. */
export const isHandoffArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.handoff;

/** True if the message is a "document received" confirmation card (patient-uploaded file awaiting staff review). */
export const isDocumentReceivedArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.documentReceived;

/** True if the message is a subscription-status card (membership info + actions). */
export const isSubscriptionArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.subscription;

/** True if the message is a "choose your primary practitioner" launcher card. */
export const isChoosePrimaryArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.choosePrimary;

/** True if the message is a "join the cancellation list" launcher card. */
export const isCancellationListArtifact = (message: WithAttrs): boolean =>
  getArtifactType(message) === CAREY_ARTIFACT.cancellationList;

export interface CareySummarySection {
  // null for the leading narrative chunk, which has no field label. Surfaces that lack a heading
  // (the queue tooltips) substitute a "Carey Summary" label; surfaces with their own heading (the
  // in-thread cards) render it as plain lead text.
  label: string | null;
  value: string;
}

/**
 * Parse a Carey staff summary into labelled sections. Carey emits the summary as `|`-separated
 * chunks, each typically "Label: value"; the leading narrative chunk has no field label (null).
 * Shared by the email (helpdesk) and chat summary surfaces, which carry the same harmonised
 * backend format (`HelpdeskMessage.carey_summary` / the handoff artifact's `staff_summary`).
 */
export const parseCareySummarySections = (summary: string | undefined): CareySummarySection[] => {
  if (!summary) return [];
  return summary
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const sep = part.indexOf(':');
      const label = sep > 0 ? part.slice(0, sep).trim() : '';
      // Only treat the text before the colon as a field label when it's short and label-like —
      // a narrative sentence that happens to contain a colon shouldn't become a label.
      const isFieldLabel = label.length > 0 && label.length <= 25 && !label.includes('.');
      return isFieldLabel
        ? { label, value: part.slice(sep + 1).trim() }
        : { label: null, value: part };
    });
};

/**
 * Carey email-helpdesk thread ownership states. Mirrors `HelpdeskMessage.carey_status`
 * in the backend swagger. Carey owns a thread while it is 'handling' or 'awaiting_patient'
 * (hidden from the admin action queue); 'needs_admin' is the default admin working queue;
 * 'quarantined' is the spam bucket. Legacy threads have carey_status undefined and are
 * treated as admin-visible. NOTE: the backend only sets 'handling' / 'awaiting_patient' once
 * the Carey email feature flag is on, so hiding these is a no-op until backend go-live — but
 * this FE filter should still only ship after the backend feature is live.
 */
export const CAREY_HELPDESK_STATUS = {
  handling: 'handling',
  awaitingPatient: 'awaiting_patient',
  needsAdmin: 'needs_admin',
  quarantined: 'quarantined',
} as const;

type WithCareyStatus = { carey_status?: string } | undefined;

/** True if Carey currently owns the thread (hidden from the admin action queue). */
export const isCareyOwnedHelpdeskMessage = (message: WithCareyStatus): boolean => {
  const status = message?.carey_status;
  return (
    status === CAREY_HELPDESK_STATUS.handling || status === CAREY_HELPDESK_STATUS.awaitingPatient
  );
};

/** True if the thread is in the spam/quarantine bucket. */
export const isQuarantinedHelpdeskMessage = (message: WithCareyStatus): boolean =>
  message?.carey_status === CAREY_HELPDESK_STATUS.quarantined;

/**
 * True if the thread should appear in the admin action queue: anything that is not
 * Carey-owned and not quarantined. Legacy threads (carey_status undefined) and
 * 'needs_admin' threads are admin-visible.
 */
export const isAdminVisibleHelpdeskMessage = (message: WithCareyStatus): boolean =>
  !isCareyOwnedHelpdeskMessage(message) && !isQuarantinedHelpdeskMessage(message);
